<template>
  <div style="min-height: 80vh">
    <expert-serp :results-object="resultsObject" :search-error="searchError" />
  </div>
</template>

<script setup>
import _ from 'lodash';
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';

import { url } from '@/url';
import { api } from '@/api';
import { entityConfigs } from '@/entityConfigs';
import { filtersFromUrlStr } from '@/filterConfigs';

import ExpertSerp from '@/components/ExpertSerp.vue';

defineOptions({ name: 'Serp' });

const store = useStore();
const route = useRoute();
const router = useRouter();

// Data
const resultsFilters = ref([]);
const resultsObject = ref(null);
const searchError = ref(null);

const selectedEntityType = computed(() => route.params.entityType);
const selectedEntityTypeConfig = computed(() => entityConfigs[selectedEntityType.value]);

useHead({
  title: computed(() => _.capitalize(selectedEntityTypeConfig.value.displayName) + ' search'),
});

const userId = computed(() => store.getters['user/userId']);
const userSavedSearches = computed(() => store.getters['user/userSavedSearches']);
const oqlFlag = computed(() => !!store.getters.featureFlags['oql']);

watch(
  () => route.params.entityType,
  (to) => {
    store.state.entityType = to;
  },
  { immediate: true }
);

// Adopt a deep-link `per_page` into the page-size store (session override) as
// soon as it appears in the URL, BEFORE the fetch watcher below runs (registration
// order is preserved for immediate watchers), so the first request uses the linked
// size. Kept separate from the fetch watcher so the store commit it makes can't
// re-trigger that watcher into a double fetch.
watch(
  () => route.query.per_page,
  () => url.adoptPerPageFromUrl(route),
  { immediate: true }
);

watch(
  // Also key on the page sizes (list + table are independent): changing the page
  // size on page 1 leaves the URL unchanged (per_page only rides the URL on deep
  // pages), so fullPath alone wouldn't fire — the results would never reload. Vue
  // coalesces multiple source changes in one tick into a single callback, so
  // deep-page navigations (where both change) still fetch only once.
  [() => route.fullPath, () => store.state.serpPageSize, () => store.state.serpTablePageSize],
  async () => {
    if (
      route.query.id &&
      !userSavedSearches.value.find((s) => s.id === route.query.id)
    ) {
      url.pushToRoute(router, {
        name: 'Serp',
        query: { ...route.query, id: undefined },
      });
      return;
    }

    if (userId.value) {
      store.commit('user/setActiveSearchId', route.query.id);
    }

    store.state.isLoading = true;

    // OQL submit (#373, Option B): when `?oql=` is present, run it via the
    // execute endpoint instead of the URL-driven /works?filter=… path — OXURL is
    // a lossy subset of OQL, so the URL can't carry nested boolean trees. If the
    // query IS URL-expressible (meta.x_query.url present), upgrade the route to
    // that OXURL form so the chips render and the URL is shareable/editable;
    // otherwise show results in read-only "advanced query" mode (no chips).
    if (oqlFlag.value && route.query.oql) {
      try {
        const resp = await api.executeOql(route.query.oql);
        const urlForm = resp?.meta?.x_query?.url;
        const upgraded = urlForm ? url.routeFromOxurl(urlForm) : null;
        if (upgraded) {
          store.commit('setOqlSubmitError', null);
          // Re-fires this watcher on a route without `oql` → normal chip path.
          await url.replaceToRoute(router, upgraded);
          return;
        }
        resultsObject.value = resp;
        store.state.resultsObject = resp;
        store.commit('setOqlSubmitError', null);
        searchError.value = null;
      } catch (e) {
        resultsObject.value = null;
        store.state.resultsObject = null;
        const validation = e?.response?.data?.validation || null;
        store.commit('setOqlSubmitError', validation);
        searchError.value =
          validation?.errors?.[0]?.message || e?.message || 'OQL query failed.';
      }
      resultsFilters.value = [];
      store.state.isLoading = false;
      window.scroll(0, 0);
      return;
    }

    try {
      // makeApiUrl is INSIDE the try block so a synchronous throw — e.g.
      // filtersFromUrlStr → createSimpleFilter on a URL filter whose key has
      // no facetConfig for this entity type (`?filter=is_oa:true` on /works,
      // since is_oa is registered for /sources and /locations only) —
      // surfaces inline via searchError instead of silently killing the
      // watcher. Caught during oxjob #228 QA-051: combined
      // `?filter=collection:X,is_oa:true` rendered a completely empty SERP
      // with no chips, facets, or error message because the throw bypassed
      // the request flow entirely. Same shape would break any URL containing
      // an unknown filter key (typo, doc copy-paste, old saved search).
      const apiQuery = url.makeApiUrl(route);
      const resp = await api.getResultsList(apiQuery);
      resultsObject.value = resp;
      store.state.resultsObject = resp;
      searchError.value = null;
    } catch (e) {
      resultsObject.value = null;
      store.state.resultsObject = null;
      // Surface the API's message field (e.g. wrong-entity-type collection
      // filter, malformed boolean search) instead of silently rendering an
      // empty SERP. Falls back to generic copy if the body is missing.
      searchError.value =
        e?.response?.data?.message ||
        e?.message ||
        'Search failed.';
    }
    store.state.isLoading = false;

    // filtersFromUrlStr can throw on an unknown filter key — same shape as
    // makeApiUrl above. Guard so the chip strip doesn't crash the watcher.
    try {
      resultsFilters.value = filtersFromUrlStr(
        selectedEntityType.value,
        route.query.filter
      );
    } catch (e) {
      resultsFilters.value = [];
    }

    window.scroll(0, 0);
  },
  { immediate: true }
);
</script>


<style lang="scss">
.v-pagination__item, .v-pagination__navigation {
  box-shadow: none;
}
table.serp-results-table {
  border-collapse: collapse;

  tr {
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, .05)
    }
  }

  td {
    border: none;
    margin: 0;
    padding: 5px 10px;

    &.range {
      text-align: right;
    }

    &.boolean {
      text-align: center;
    }
  }

}
</style>
